import type {IXHRLoaderConfig, XHRCancelToken} from "../utils/loader/XHRLoader";
import {XHRLoader} from "../utils/loader/XHRLoader";
import {ObjectUtils} from "../utils/data/ObjectUtils";
import {Signal} from "../utils/signal/Signal";
import type {IConfig} from "../IConfig";
import type {IIconConfig} from "../ui/modules/catalog/create/CatalogTypes";
import type {
	GetIconXyiconCatalogDto,
	PortTemplateDto,
	SpaceFileDto,
	XyiconFeature,
	UpdateSpaceFileRequest,
	UpdateXyiconCatalogIconRequest,
	XyiconCatalogsUpdateIconDto,
	UserDeletedDto,
	UpdateBoundaryTypeRequest,
	UpdateBoundaryTypeDto,
	PortfolioNameDto,
} from "../generated/api/base";
import type {Catalog} from "./models/Catalog";
import type {Space} from "./models/Space";
import type {Portfolio} from "./models/Portfolio";
import type {Boundary} from "./models/Boundary";
import type {IXyiconPropertyRequest} from "./state/AppActions";
import {PermissionService} from "./services/PermissionService";
import {LocalStorageService} from "./services/LocalStorageService";
import {AuthService} from "./services/AuthService";
import type {AppState} from "./state/AppState";
import {TypeAndFieldService} from "./services/TypeAndFieldService";
import {ViewService} from "./services/ViewService";
import {FeatureService} from "./services/FeatureService";
import {LayoutService} from "./services/LayoutService";
import {SignalRLayer} from "./signalr/SignalRLayer";
import {DocumentService} from "./services/DocumentService";
import type {ViewFolderStructure} from "./models/ViewUtils";

type ApiName = "base" | "reports" | "search";

export interface IError {
	ErrorMessage: string; // Authentication failed. User not found or invalid username/e-mail and password
	ErrorCode: string; // USER_AUTHENTICATION_FAILED
	StackTrace: string; //    at SpaceRunner.V4.Business.Actions.Authentication.SignInCommandHandler.Handle(SignInCommand request, CancellationToken cancellationToken) in D:\\a\\1\\s\\Source\\SpaceRunner V4 Backend\\SpaceRunner.V4.Business\\Actions\\Authentication\\SignInCommandHandler.cs:line 174\r\n   at MediatR.Pipeline.RequestExceptionProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\r\n   at MediatR.Pipeline.RequestExceptionProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\r\n   at MediatR.Pipeline.RequestExceptionActionProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\r\n   at MediatR.Pipeline.RequestExceptionActionProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\r\n   at MediatR.Pipeline.RequestPostProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\r\n   at MediatR.Pipeline.RequestPreProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\r\n   at SpaceRunner.V4.API.Controllers.AuthController.SignIn(SignInRequest request) in D:\\a\\1\\s\\Source\\SpaceRunner V4 Backend\\SpaceRunner.V4.API\\Controllers\\AuthController.cs:line 46\r\n   at lambda_method83(Closure , Object )\r\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ActionMethodExecutor.AwaitableObjectResultExecutor.Execute(IActionResultTypeMapper mapper, ObjectMethodExecutor executor, Object controller, Object[] arguments)\r\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ControllerActionInvoker.<InvokeActionMethodAsync>g__Logged|12_1(ControllerActionInvoker invoker)\r\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ControllerActionInvoker.<InvokeNextActionFilterAsync>g__Awaited|10_0(ControllerActionInvoker invoker, Task lastTask, State next, Scope scope, Object state, Boolean isCompleted)\r\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ControllerActionInvoker.Rethrow(ActionExecutedContextSealed context)\r\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ControllerActionInvoker.Next(State& next, Scope& scope, Object& state, Boolean& isCompleted)\r\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ControllerActionInvoker.<InvokeInnerFilterAsync>g__Awaited|13_0(ControllerActionInvoker invoker, Task lastTask, State next, Scope scope, Object state, Boolean isCompleted)\r\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ResourceInvoker.<InvokeFilterPipelineAsync>g__Awaited|20_0(ResourceInvoker invoker, Task lastTask, State next, Scope scope, Object state, Boolean isCompleted)\r\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ResourceInvoker.<InvokeAsync>g__Logged|17_1(ResourceInvoker invoker)\r\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ResourceInvoker.<InvokeAsync>g__Logged|17_1(ResourceInvoker invoker)\r\n   at Microsoft.AspNetCore.Routing.EndpointMiddleware.<Invoke>g__AwaitRequestTask|6_0(Endpoint endpoint, Task requestTask, ILogger logger)\r\n   at Microsoft.AspNetCore.Authorization.AuthorizationMiddleware.Invoke(HttpContext context)\r\n   at SpaceRunner.V4.API.Middleware.SRAccessTokenValidatorMiddleware.Invoke(HttpContext context, IUserPermissionService currentUserService, ISpaceRunnerDbContext spaceRunnerDBContext, CacheHelper cacheHelper) in D:\\a\\1\\s\\Source\\SpaceRunner V4 Backend\\SpaceRunner.V4.API\\Middleware\\SRAccessTokenValidatorMiddleware.cs:line 104\r\n   at Microsoft.AspNetCore.Authentication.AuthenticationMiddleware.Invoke(HttpContext context)\r\n   at Microsoft.AspNetCore.ResponseCompression.ResponseCompressionMiddleware.InvokeCore(HttpContext context)\r\n   at SpaceRunner.V4.API.Middleware.SRSignalRProcessorMiddleware.Invoke(HttpContext context, IEnumerable`1 notifyServices) in D:\\a\\1\\s\\Source\\SpaceRunner V4 Backend\\SpaceRunner.V4.API\\Middleware\\SRSignalRProcessorMiddleware.cs:line 39\r\n   at Microsoft.AspNetCore.Diagnostics.StatusCodePagesMiddleware.Invoke(HttpContext context)\r\n   at Microsoft.AspNetCore.Diagnostics.ExceptionHandlerMiddleware.<Invoke>g__Awaited|6_0(ExceptionHandlerMiddleware middleware, HttpContext context, Task task)
}

export interface IResponse {
	result: any;
	error: IError;
}

export class TransportLayer {
	public readonly signals = {
		error: Signal.create<XHRLoader>(),
		switchOrganization: Signal.create(),
	};

	private readonly _config: IConfig;

	private readonly _services = {
		localStorage: new LocalStorageService(this),
		auth: new AuthService(this),
		permission: new PermissionService(this),
		feature: new FeatureService(this),
		typefield: new TypeAndFieldService(this),
		view: new ViewService(this),
		fieldLayout: new LayoutService(this),
		document: new DocumentService(this),
	};

	private _signalR: SignalRLayer;
	private readonly _state: AppState;

	/** Needed for getting files from server */
	private _generalSettings: {
		azureRootURL: string;
		token: string;
	};

	constructor(state: AppState, config: IConfig) {
		this._state = state;
		this._config = config;
	}

	public init() {
		this._signalR = new SignalRLayer(this);
	}

	public async initGeneralSettings() {
		const {result, error} = await this.request({url: "config/generalsettings"});

		this._generalSettings = result;
	}

	/**
	 * @param filePath eg.: icons/pH14628692.svg
	 */
	public getFullPathFromServer(filePath: string, forOrganization: boolean = true, orgId: string = null) {
		let baseURL = `${this._generalSettings.azureRootURL}`;

		if (forOrganization) {
			baseURL += `/organizations/${orgId ?? this.services.auth.authData.organizationID}`;
		}

		return `${baseURL}/${filePath}?${this._generalSettings.token}`;
	}

	public getReportFilePath(requestId: string, extension = "csv") {
		return this.getFullPathFromServer(`uploads/reports/${requestId}.${extension}`);
	}

	public getCurrentOrganizationLogo(): string {
		return this.getOrganizationLogoById(this._state.organizationIdByAuthData);
	}

	public getOrganizationLogoById(id: string): string {
		const org = this.appState.organizations.find((o) => o.id === id);

		return org?.settings?.logoFileName ? `${this.getFullPathFromServer(org.settings.logoFileName, true, id)}&noCache=${org?.lastModifiedAt}` : "";
	}

	// GET

	public get<T = any>(config: {url: string; params?: any; headers?: any; apiName?: ApiName; cancelToken?: XHRCancelToken}) {
		return this.request<T>(
			{
				url: config.url,
				method: XHRLoader.METHOD_GET,
				params: config.params,
				headers: config.headers,
				cancelToken: config.cancelToken,
			},
			config.apiName ?? "base",
		);
	}

	public requestForOrganization<T = any>(config: IXHRLoaderConfig, apiName: ApiName = "base") {
		config.params = config.params || {};
		config.params.organizationID = this.services.auth.authData.organizationID;
		return this.request<T>(config, apiName);
	}

	public async request<T = any>(config: IXHRLoaderConfig, apiName: ApiName = "base") {
		let result: T = undefined;
		let error: IError = null;

		const cancelToken = config.cancelToken;

		const isFormData = typeof (config?.params as FormData)?.append === "function";
		if (!isFormData) {
			config = ObjectUtils.clone(config, true);
		}
		config.cancelToken = cancelToken;

		const baseUrl = this._config.api[apiName];

		config.url = `${baseUrl}/${config.url}`;

		if (config.json === undefined) {
			config.json = true;
		}

		// TODO clarify this condition
		if (!(config.url.indexOf("token") !== -1 && config.method === XHRLoader.METHOD_POST)) {
			const authData = this._services.auth.authData;

			if (authData.accessToken) {
				config.headers = config.headers || {};
				config.headers["Authorization"] = `${authData.tokenType} ${authData.accessToken}`;
				config.headers["Cache-Control"] = "no-cache";

				// https://dev.azure.com/xyicon/SpaceRunner%20V4/_wiki/wikis/SpaceRunner-V4.wiki/15/Excluding-a-SignalR-connection-from-being-notified
				if (this._signalR.connectionId) {
					config.headers["SR-SLiveID"] = this._signalR.connectionId;
				}
			}
		}

		try {
			result = await XHRLoader.loadAsync<T>(config);
		} catch (_xhr) {
			const xhr = _xhr as XHRLoader;

			error = xhr.result;
			if (xhr.resultMessage === "aborted") {
				console.log(`Request aborted: ${xhr.config.url}`);
				error = {
					ErrorCode: "ABORTED",
					ErrorMessage: "Aborted",
					StackTrace: "",
				};
			}

			if (xhr.status === 401) {
				if (!["NEW_USER_TOKEN_INVALID_REJECTED_BY_SERVER", "PASSWORD_INCORRECT"].includes(error.ErrorCode)) {
					this._services.auth.logout();
				}
			}

			if (error) {
				console.warn("Error", error);
			}

			if (config.onFail) {
				config.onFail(xhr);
			}
		}

		return {result, error};
	}

	public async getIconConfigOfCatalog(catalog: Catalog): Promise<IIconConfig> {
		const {result: iconData, error} = await this.requestForOrganization<GetIconXyiconCatalogDto>({
			url: "xyiconcatalogs/icondata",
			method: XHRLoader.METHOD_GET,
			params: {
				xyiconCatalogID: catalog.id,
			},
		});

		if (!iconData.xyiconCatalogsIcon) {
			console.warn(`iconData.xyiconCatalogsIcon doesn't exist. Please report this to the developers.`);
		}

		return ObjectUtils.apply(iconData.xyiconCatalogsIcon, {iconCategory: catalog.iconCategory}) as IIconConfig;
	}

	public async updateIconConfig(catalog: Catalog, iconConfig: IIconConfig, thumbnail: string) {
		const updateData: UpdateXyiconCatalogIconRequest = {
			xyiconCatalogID: catalog.id,
			iconType: iconConfig.iconCategory,
			iconData: {
				defaultIcon: iconConfig.defaultIcon,
				uploadedIcon: iconConfig.uploadedIcon,
				modelParameters: iconConfig.modelParameters,
			},
			generatedIcon: thumbnail,
		};

		await this.requestForOrganization<XyiconCatalogsUpdateIconDto>({
			url: "xyiconcatalogs/updateicon",
			method: XHRLoader.METHOD_POST,
			params: updateData,
		});

		catalog.updateIconData(iconConfig, iconConfig.iconCategory, thumbnail);
		await this._state.app.spaceViewRenderer.xyiconManager.updateCatalog(catalog);
	}

	public async updatePortTemplate(catalog: Catalog, portTemplate: PortTemplateDto[]) {
		catalog.setPortTemplate(portTemplate);

		await this.requestForOrganization({
			url: "xyiconcatalogs/updateporttemplate",
			method: XHRLoader.METHOD_POST,
			params: {
				xyiconCatalogID: catalog.id,
				portTemplate: portTemplate,
			},
		});
	}

	public async duplicatePortfolio(portfolio: Portfolio) {
		const {result, error} = await this.requestForOrganization({
			url: "portfolios/duplicate",
			method: XHRLoader.METHOD_POST,
			params: {
				portfolioIDList: [portfolio.id],
			},
		});

		return result;
	}

	public async updateSpaceType(space: Space | Space[]) {
		const spaceArray = Array.isArray(space) ? [...space] : [space];
		const {result, error} = await this.requestForOrganization({
			url: "spaces/updatetype",
			method: XHRLoader.METHOD_POST,
			params: {
				spaceIDList: spaceArray.map((s) => s.id),
				spaceTypeID: spaceArray[0].typeId,
				portfolioID: spaceArray[0].portfolioId,
			},
		});

		if (result && !error) {
			spaceArray.forEach((p) => p.applyUpdate(result));
		}

		return result;
	}

	public async updateSpaceFiles(params: UpdateSpaceFileRequest) {
		const {result, error} = await this.requestForOrganization<SpaceFileDto>({
			url: "spacefiles/update",
			method: XHRLoader.METHOD_POST,
			params: params,
		});

		return result;
	}

	public async updateXyiconPosition(requestObj: IXyiconPropertyRequest) {
		const {result, error} = await this.requestForOrganization({
			url: "xyicons/updateposition",
			method: XHRLoader.METHOD_POST,
			params: {
				portfolioID: requestObj.portfolioID,
				spaceID: requestObj.spaceID,
				xyiconPositionList: requestObj.list,
			},
		});

		return result;
	}

	public async updateXyiconOrientation(requestObj: IXyiconPropertyRequest) {
		const {result, error} = await this.requestForOrganization({
			url: "xyicons/updateorientation",
			method: XHRLoader.METHOD_POST,
			params: {
				portfolioID: requestObj.portfolioID,
				spaceID: requestObj.spaceID,
				xyiconOrientationList: requestObj.list,
			},
		});

		return result;
	}

	public async updateCatalogType(catalog: Catalog | Catalog[]) {
		const catalogArray = Array.isArray(catalog) ? [...catalog] : [catalog];
		const {result, error} = await this.requestForOrganization({
			url: "xyiconcatalogs/updatetype",
			method: XHRLoader.METHOD_POST,
			params: {
				updatedXyiconCatalogs: catalogArray.map((c) => ({xyiconCatalogID: c.id, xyiconTypeID: c.typeId})),
			},
		});

		if (result && !error) {
			catalogArray.forEach((catalogInstance) => {
				const catalogData = result.find((r: {xyiconCatalogID: string}) => r.xyiconCatalogID === catalogInstance.id);

				if (catalogData) {
					const transformedCatalogData = {
						lastModifiedAt: catalogData.lastModifiedAt,
						lastModifiedBy: catalogData.lastModifiedBy,
						xyiconCatalogIDList: catalogData.xyiconCatalogID ? [catalogData.xyiconCatalogID] : null,
					};
					catalogInstance.applyUpdate(transformedCatalogData);
				}
			});
		}

		return result;
	}

	public async updateBoundaryType(boundary: Boundary | Boundary[]) {
		const boundaryArray = Array.isArray(boundary) ? [...boundary] : [boundary];
		const params: UpdateBoundaryTypeRequest = {
			boundaryIDList: boundaryArray.map((b) => b.id),
			boundaryTypeID: boundaryArray[0].typeId,
			portfolioID: boundaryArray[0].portfolioId,
		};
		const {result, error} = await this.requestForOrganization<UpdateBoundaryTypeDto>({
			url: "boundaries/updatetype",
			method: XHRLoader.METHOD_POST,
			params,
		});

		if (result && !error) {
			boundaryArray.forEach((p) => p.applyUpdate(result));
		}

		return result;
	}

	public async updatePortfolioType(portfolio: Portfolio | Portfolio[]) {
		const portfolioArray = Array.isArray(portfolio) ? [...portfolio] : [portfolio];
		const {result, error} = await this.requestForOrganization({
			url: "portfolios/updatetype",
			method: XHRLoader.METHOD_POST,
			params: {
				portfolioTypeID: portfolioArray[0].typeId,
				portfolioIDList: portfolioArray.map((p) => p.id),
			},
		});

		if (result && !error) {
			portfolioArray.forEach((p) => p.applyUpdate(result));
		}

		return result;
	}

	public async getPortfolioNamesByPortfolioIDs(portfolioIDList: string[]): Promise<PortfolioNameDto[]> {
		const {result, error} = await this.request<PortfolioNameDto[]>({
			url: "portfolios/names",
			method: XHRLoader.METHOD_POST,
			params: {
				organizationID: this.services.auth.authData.organizationID,
				portfolioIDList,
			},
		});

		if (error) {
			console.error(error);
			return [];
		}

		return result ?? [];
	}

	public async transferUserOwnershipsToOneUser(userIDList: string[], newOwnerUserID: string) {
		const {result, error} = await this.requestForOrganization({
			url: "users/updatesharedobjectowner",
			method: XHRLoader.METHOD_POST,
			params: {
				userIDList: userIDList || [],
				newOwnerUserID: newOwnerUserID || "",
			},
		});

		return {result, error};
	}

	public async saveViewFolderStructure(feature: XyiconFeature, viewFolderStructure: ViewFolderStructure) {
		const {result, error} = await this.requestForOrganization<ViewFolderStructure>({
			url: "users/updateviewpreferences",
			params: {
				viewPreferences: viewFolderStructure,
				feature: feature,
			},
			method: XHRLoader.METHOD_POST,
		});

		return {result, error};
	}

	public async getRemovedUsersList(): Promise<UserDeletedDto[]> {
		const {result, error} = await this.requestForOrganization<UserDeletedDto[]>({
			url: "users/removed",
			method: XHRLoader.METHOD_GET,
		});

		if (error) {
			console.error(error);
			return [];
		}

		return result ?? [];
	}

	public get apiUrl() {
		return this._config.api.base;
	}

	public get signalRUrl() {
		return this._config.api.signalR;
	}

	public get services() {
		return this._services;
	}

	public get appState() {
		return this._state;
	}

	public get signalR() {
		return this._signalR;
	}

	public get config() {
		return this._config;
	}
}
